import {
	getClasslinkLoginClientId,
	getClasslinkLoginRedirectUri,
	getGoogleLoginClientId,
	getMicrosoftLoginClientId,
} from "../utils/getLoginClientId";

export enum LoginProvider {
	GOOGLE = "google",
	MICROSOFT = "azuread",
	CLASSLINK = "classlink",
}

/**
 * Generates OAuth login URLs for different providers using their respective client IDs
 * @param provider - The OAuth provider (google, azure, classlink)
 * @param options - Additional options for the login URL
 * @returns The complete OAuth login URL
 */
function generateLoginUrl(provider: LoginProvider, deviceId?: string): string {
	// Get the appropriate client ID based on the current environment
	const clientId = getClientIdForProvider(provider);
	const redirectUri = getRedirectUrlForProvider(provider);

	switch (provider) {
		case LoginProvider.GOOGLE:
			return generateGoogleLoginUrl(clientId, redirectUri, deviceId);
		case LoginProvider.MICROSOFT:
			return generateMicrosoftLoginUrl(clientId, redirectUri, deviceId);
		case LoginProvider.CLASSLINK:
			return generateClasslinkLoginUrl(clientId, redirectUri, deviceId);
		default:
			throw new Error(`Unsupported login provider: ${provider}`);
	}
}

/**
 * Gets the appropriate client ID for the given provider based on current environment
 */
function getClientIdForProvider(provider: LoginProvider): string {
	switch (provider) {
		case LoginProvider.GOOGLE:
			return getGoogleLoginClientId(window.location.hostname);
		case LoginProvider.MICROSOFT:
			return getMicrosoftLoginClientId(window.location.hostname);
		case LoginProvider.CLASSLINK:
			return getClasslinkLoginClientId(window.location.hostname);
		default:
			throw new Error(`Unsupported provider: ${provider}`);
	}
}

/**
 * Generates Google OAuth login URL
 */
function generateGoogleLoginUrl(clientId: string, redirectUri: string, deviceId?: string): string {
	const params = new URLSearchParams({
		client_id: clientId,
		redirect_uri: redirectUri,
		response_type: "code",
		scope: "openid email",
		state: deviceId ? deviceId : "",
		prompt: "select_account",
	});

	return `https://accounts.google.com/o/oauth2/auth?${params.toString()}`;
}

/**
 * Generates Microsoft OAuth login URL
 */
function generateMicrosoftLoginUrl(clientId: string, redirectUri: string, deviceId?: string): string {
	const params = new URLSearchParams({
		client_id: clientId,
		redirect_uri: redirectUri,
		response_type: "code",
		state: deviceId ? deviceId : "",
	});

	return `https://login.microsoftonline.com/common/oauth2/authorize?${params.toString()}`;
}

/**
 * Generates ClassLink OAuth login URL
 */
function generateClasslinkLoginUrl(clientId: string, redirectUri: string, deviceId?: string): string {
	const params = new URLSearchParams({
		client_id: clientId,
		redirect_uri: redirectUri,
		response_type: "code",
		scope: "full",
		state: deviceId ? deviceId : "",
	});

	return `https://launchpad.classlink.com/oauth2/v2/auth?${params.toString()}`;
}

function getRedirectUrlForProvider(provider: LoginProvider): string {
	const origin = window.location.origin;
	let redirectUrl = origin + "/login/" + provider;
	const hostname = window.location.hostname;
	if (provider === LoginProvider.CLASSLINK) {
		redirectUrl = getClasslinkLoginRedirectUri(hostname) + "/login/" + provider;
	}
	if (hostname.includes("localhost") || hostname.includes("devlogin.linewize.net")) {
		redirectUrl = "https://devlogin.linewize.net:8081/login/" + provider;
	}
	return redirectUrl;
}

export const loginUrls = {
	google: (deviceId?: string): string => generateLoginUrl(LoginProvider.GOOGLE, deviceId),
	microsoft: (deviceId?: string): string => generateLoginUrl(LoginProvider.MICROSOFT, deviceId),
	classlink: (deviceId?: string): string => generateLoginUrl(LoginProvider.CLASSLINK, deviceId),
};
